<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { api } from "../api";

type ServiceModule = "online" | "management";

const route = useRoute();
const loading = ref(true);
const errorMessage = ref("");
const launchUrl = ref("");
const frameKey = ref(0);

const serviceModule = computed<ServiceModule>(() => route.path.includes("/manage/") ? "management" : "online");
const activeView = computed(() => String(route.params.view ?? (serviceModule.value === "management" ? "workgroups" : "current")));
const moduleTitle = computed(() => serviceModule.value === "management" ? "客服管理" : "在线客服");

async function connect() {
  loading.value = true;
  errorMessage.value = "";
  try {
    const session = await api.bytedeskSession(serviceModule.value, activeView.value);
    if (!session.launchUrl.startsWith("https://kefu-test.toy-encyclopedia.com/")) {
      throw new Error("客服系统地址校验失败");
    }
    const embeddedUrl = new URL(session.launchUrl);
    embeddedUrl.searchParams.set("wjbkEmbed", "1");
    launchUrl.value = embeddedUrl.toString();
    frameKey.value += 1;
  } catch (error) {
    launchUrl.value = "";
    errorMessage.value = error instanceof Error ? error.message : "连接客服系统失败";
  } finally {
    loading.value = false;
  }
}

function openStandalone() {
  if (!launchUrl.value) return;
  const standaloneUrl = new URL(launchUrl.value);
  standaloneUrl.searchParams.delete("wjbkEmbed");
  window.open(standaloneUrl.toString(), "_blank", "noopener,noreferrer");
}

watch(() => route.fullPath, connect, { immediate: true });
</script>

<template>
  <section class="embedded-system-shell">
    <div class="embedded-system-toolbar">
      <div>
        <strong>{{ moduleTitle }}</strong>
        <span>使用客服系统原生目录与功能</span>
      </div>
      <div class="embedded-system-actions">
        <button type="button" @click="connect">刷新连接</button>
        <button type="button" @click="openStandalone">独立窗口</button>
      </div>
    </div>

    <div v-if="loading" class="embedded-system-state">正在进入{{ moduleTitle }}…</div>
    <div v-else-if="errorMessage" class="embedded-system-state embedded-system-error">
      <strong>暂时无法进入{{ moduleTitle }}</strong>
      <p>{{ errorMessage }}</p>
      <button type="button" @click="connect">重试</button>
    </div>
    <iframe
      v-else
      :key="frameKey"
      :src="launchUrl"
      :title="moduleTitle"
      allow="clipboard-read; clipboard-write; microphone; camera; notifications"
      referrerpolicy="strict-origin-when-cross-origin"
    ></iframe>
  </section>
</template>

<style scoped>
.embedded-system-shell { min-height: 650px; height: calc(100vh - 96px); display: flex; flex-direction: column; overflow: hidden; background: #fff; border: 1px solid #e3e8f1; border-radius: 14px; box-shadow: 0 7px 24px rgba(20, 35, 68, .055); }
.embedded-system-toolbar { min-height: 56px; padding: 9px 14px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid #e8ecf3; background: #fff; }
.embedded-system-toolbar strong, .embedded-system-toolbar span { display: block; }
.embedded-system-toolbar strong { color: #202c45; font-size: 14px; }
.embedded-system-toolbar span { margin-top: 3px; color: #8a95a8; font-size: 11px; }
.embedded-system-actions { display: flex; gap: 8px; }
.embedded-system-actions button, .embedded-system-state button { border: 0; border-radius: 8px; padding: 7px 11px; color: #315efb; background: #edf2ff; cursor: pointer; font-size: 11px; }
.embedded-system-state { flex: 1; display: grid; place-content: center; text-align: center; color: #7c879c; font-size: 13px; }
.embedded-system-error p { margin: 8px 0 14px; }
.embedded-system-shell iframe { width: 100%; flex: 1; border: 0; background: #f4f6fa; }
@media (max-width: 680px) {
  .embedded-system-toolbar { align-items: flex-start; flex-wrap: wrap; }
}
</style>
